// src/pages/qc/RmNcDetailModal.jsx
import { useState } from "react";
import { ImageUpload } from "../../components/ImageUpload";
import { todayDubai } from "../../utils/dateUtils";

const TODAY = todayDubai();

const DISPOSITION_LABELS = {
  ACCEPT:               "Accept",
  ACCEPT_CONDITIONALLY: "Accept Conditionally",
  REJECT_RETURN:        "Reject and Return to Supplier",
  SCRAP:                "Scrap",
  DESTROY:              "Destroy",
};

const STATUS_STYLE = {
  OPEN:                { bg: "#fef3c7", color: "#92400e" },
  SUPPLIER_RESPONDED:  { bg: "#dbeafe", color: "#1e40af" },
  CLOSED:              { bg: "#d1fae5", color: "#065f46" },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] ?? { bg: "#f3f4f6", color: "#374151" };
  const label = status === "OPEN" ? "Open"
              : status === "SUPPLIER_RESPONDED" ? "Supplier Responded"
              : status === "CLOSED" ? "Closed" : status;
  return (
    <span style={{ background: s.bg, color: s.color, borderRadius: 4,
      padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>
      {label}
    </span>
  );
}

function Field({ label, value }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13 }}>{value || "—"}</div>
    </div>
  );
}

function TwoCol({ children }) {
  return <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>{children}</div>;
}

function SectionHeader({ title }) {
  return (
    <div style={{ background: "#1a3a6b", color: "#fff", fontWeight: 600, fontSize: 12,
      padding: "5px 10px", margin: "14px 0 10px", borderRadius: 3 }}>
      {title}
    </div>
  );
}

function YNSelect({ label, value, onChange }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label className="form-label">{label}</label>
      <select className="form-select" value={value === null ? "" : String(value)}
        onChange={e => onChange(e.target.value === "" ? null : e.target.value === "true")}>
        <option value="">— Select —</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    </div>
  );
}

// ── Supplier Response sub-form ────────────────────────────────────────────────
function SupplierTab({ nc, onSave, onSaved, canEdit }) {
  const [form, setForm] = useState({
    rootCause:         nc.rootCause         || "",
    correctiveAction:  nc.correctiveAction  || "",
    implDate:          nc.implDate          || "",
    implPerson:        nc.implPerson        || "",
    supplierSignature: nc.supplierSignature || "",
    supplierRespDate:  nc.supplierRespDate  || TODAY,
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const body = {
        ...form,
        implDate:         form.implDate         || null,
        supplierRespDate: form.supplierRespDate  || null,
      };
      await onSave(body);
      onSaved();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  if (!canEdit && nc.status !== "OPEN") {
    return (
      <div>
        <TwoCol>
          <Field label="Root Cause"         value={nc.rootCause} />
          <Field label="Corrective Action"  value={nc.correctiveAction} />
          <Field label="Implementation Date" value={nc.implDate} />
          <Field label="Person Responsible" value={nc.implPerson} />
          <Field label="Supplier Signature" value={nc.supplierSignature} />
          <Field label="Response Date"      value={nc.supplierRespDate} />
        </TwoCol>
      </div>
    );
  }

  return (
    <form onSubmit={handleSave}>
      <div className="form-group">
        <label className="form-label">Root Cause</label>
        <textarea className="form-input" rows={6} value={form.rootCause}
          onChange={e => set("rootCause", e.target.value)} />
      </div>
      <div className="form-group">
        <label className="form-label">Corrective Action</label>
        <textarea className="form-input" rows={6} value={form.correctiveAction}
          onChange={e => set("correctiveAction", e.target.value)} />
      </div>
      <div className="form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">Implementation Date</label>
          <input className="form-input" type="date" value={form.implDate}
            onChange={e => set("implDate", e.target.value)} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">Person Responsible</label>
          <input className="form-input" value={form.implPerson}
            onChange={e => set("implPerson", e.target.value)} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">Supplier Signature</label>
          <input className="form-input" value={form.supplierSignature}
            onChange={e => set("supplierSignature", e.target.value)} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">Response Date</label>
          <input className="form-input" type="date" value={form.supplierRespDate}
            onChange={e => set("supplierRespDate", e.target.value)} />
        </div>
      </div>
      {error && <div className="alert-warn">{error}</div>}
      <div style={{ marginTop: 12, textAlign: "right" }}>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? "Saving…" : "Save Supplier Response"}
        </button>
      </div>
    </form>
  );
}

// ── Close/Review sub-form ─────────────────────────────────────────────────────
function CloseTab({ nc, onClose, onSaved, canEdit }) {
  const [form, setForm] = useState({
    followupAudit: nc.followupAudit ?? null,
    caAccepted:    nc.caAccepted    ?? null,
    caEffective:   nc.caEffective   ?? null,
    reviewedBy:    nc.reviewedBy    || "",
    reviewedDate:  nc.reviewedDate  || TODAY,
    disposition:   nc.disposition   || "",
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  const yn = v => v === true ? "Yes" : v === false ? "No" : "—";

  if (nc.status === "CLOSED") {
    return (
      <div>
        <TwoCol>
          <Field label="Follow-up Audit Required" value={yn(nc.followupAudit)} />
          <Field label="Corrective Action Accepted" value={yn(nc.caAccepted)} />
          <Field label="Corrective Action Effective" value={yn(nc.caEffective)} />
          <Field label="Reviewed By"    value={nc.reviewedBy} />
          <Field label="Review Date"    value={nc.reviewedDate} />
          <Field label="Final Disposition" value={DISPOSITION_LABELS[nc.disposition] || nc.disposition} />
        </TwoCol>
      </div>
    );
  }

  if (!canEdit) return <div style={{ color: "#aaa", fontSize: 12 }}>Not closed yet.</div>;

  async function handleClose(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const body = {
        ...form,
        reviewedDate: form.reviewedDate || null,
        disposition:  form.disposition  || null,
      };
      await onClose(body);
      onSaved();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleClose}>
      <TwoCol>
        <YNSelect label="Follow-up Audit Required" value={form.followupAudit}
          onChange={v => set("followupAudit", v)} />
        <YNSelect label="Corrective Action Accepted" value={form.caAccepted}
          onChange={v => set("caAccepted", v)} />
        <YNSelect label="Corrective Action Effective" value={form.caEffective}
          onChange={v => set("caEffective", v)} />
      </TwoCol>
      <div className="form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">Reviewed By</label>
          <input className="form-input" value={form.reviewedBy}
            onChange={e => set("reviewedBy", e.target.value)} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">Review Date</label>
          <input className="form-input" type="date" value={form.reviewedDate}
            onChange={e => set("reviewedDate", e.target.value)} />
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Final Disposition (overrides if changed)</label>
        <select className="form-select" value={form.disposition}
          onChange={e => set("disposition", e.target.value)}>
          <option value="">— Keep existing —</option>
          {Object.entries(DISPOSITION_LABELS).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
      </div>
      {error && <div className="alert-warn">{error}</div>}
      <div style={{ marginTop: 12, textAlign: "right" }}>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? "Closing…" : "Close NC Report"}
        </button>
      </div>
    </form>
  );
}

// ── Main detail modal ─────────────────────────────────────────────────────────
export function RmNcDetailModal({ nc, onSupplierSave, onClose, onCancel, canEdit }) {
  const [tab, setTab] = useState("part1");
  const canEditRecord = canEdit && nc.status !== "CLOSED";

  const tabs = [
    { id: "part1",    label: "Part 1 — Details" },
    { id: "supplier", label: "Part 2 — Supplier" },
    { id: "review",   label: "Part 3 — Review" },
    { id: "images",   label: "Attachments" },
  ];

  return (
    <div className="modal" style={{ width: 640, maxHeight: "90vh", overflowY: "auto" }}>
      <div className="modal-header">
        <div>
          <div className="modal-title">{nc.ncNumber}</div>
          <StatusBadge status={nc.status} />
        </div>
        <button className="modal-close" onClick={onCancel}>✕</button>
      </div>

      {/* Tab bar */}
      <div style={{ display: "flex", borderBottom: "2px solid #e5e7eb", padding: "0 16px" }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              padding: "8px 14px", fontSize: 12, fontWeight: 600,
              color: tab === t.id ? "#1a3a6b" : "#6b7280",
              borderBottom: tab === t.id ? "3px solid #1a3a6b" : "3px solid transparent",
              marginBottom: -2,
            }}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="modal-body">
        {tab === "part1" && (
          <div>
            <TwoCol>
              <Field label="Product Name"  value={nc.productName} />
              <Field label="Batch Number"  value={nc.batchNumber} />
              <Field label="Supplier Name" value={nc.supplierName} />
              <Field label="Supply Date"   value={nc.supplyDate} />
              <Field label="Test Date"     value={nc.testDate} />
              <Field label="Reported By"   value={nc.reportedBy} />
              <Field label="Due Date"      value={nc.dueDate} />
              <Field label="Disposition"
                value={DISPOSITION_LABELS[nc.disposition] || nc.disposition} />
            </TwoCol>
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, marginBottom: 4 }}>
                Findings / Non-Conformance Description
              </div>
              <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb",
                padding: "8px 12px", borderRadius: 4, fontSize: 13, whiteSpace: "pre-wrap" }}>
                {nc.findings}
              </div>
            </div>
          </div>
        )}

        {tab === "supplier" && (
          <div>
            <SectionHeader title="Part 2 — Supplier Response" />
            <SupplierTab nc={nc} onSave={onSupplierSave} onSaved={() => setTab("part1")} canEdit={canEditRecord} />
          </div>
        )}

        {tab === "review" && (
          <div>
            <SectionHeader title="Part 3 — Al Mehraj Review" />
            <CloseTab nc={nc} onClose={onClose} onSaved={() => setTab("part1")} canEdit={canEdit} />
          </div>
        )}

        {tab === "images" && (
          <ImageUpload entityType="RMNC" entityId={nc.id} canEdit={canEditRecord} />
        )}
      </div>
    </div>
  );
}
